import {
  Component,
  OnInit,
  OnDestroy,
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Output,
  EventEmitter,
  HostBinding,
} from '@angular/core';
import { Router, NavigationEnd, RouterEvent } from '@angular/router';
import { FormControl } from '@angular/forms';
import { Observable, Subject, combineLatest, Subscription } from 'rxjs';
import {
  map,
  takeUntil,
  filter,
  take,
  startWith,
  switchMap,
} from 'rxjs/operators';
import { HttpClient } from '@angular/common/http';
import { MatDialog, MatDialogConfig } from '@angular/material/dialog';
import { resolveHostedUrl } from 'src/app/constants/hosted-api-urls';
import {
  SidenavConfiguration,
  SidenavGroup,
  SidenavItem,
  SidenavState,
} from '../../models/sidenav.interface';
import { SidenavConfigService } from '../../services/sidenav-config.service';
import { SidenavGroupsService } from '../../services/sidenav-groups.service';
import { SidenavLockService } from '../../services/sidenav-lock.service';
import { AuthService } from '../../../services/auth.service';
import { UtilService } from '../../../../util/util.service';
import { EasyEatHqService } from '../../../services/ee-hq.service';
import { LocalStorageService } from '../../../services/localStorage.service';
import { EventTrackerService } from '../../../services/event-tracker.service';
import { ThemeService } from '../../../theme/theme.service';
import { BillingCountersDataService } from '../../../services/billing-counters-data.service';
import { ApiService } from '../../../services/api.service';
import { TableBookingHelperService } from '../../../../table-bookings/services/table-bookings-helper.service';
import { TableBookingApiService } from '../../../../table-bookings/services/table-bookings-api.service';
import { PusherEventService } from '../../../services/pusher-event.service';
import { hasOrderTopup, OrderStateService } from '../../../../order/order-state.service';
import { OrderReactEventService } from '../../../../order/order-reactevent.service';
import { TranslateService } from '@ngx-translate/core';
import { AnimationControlService } from '../../../services/animation-control.service';
import { MessageService } from '../../../services/message.service';
import { ANIMATION_CONTROL_SUBJECT } from '../../../enums/animation-control.enum';
import { DateService } from '../../../services/date.service';
import { AudioService } from '../../../services/audio.service';
import { REACTEVENTS } from '../../../../react-events';
import { UserType } from '../../enums/sidenav.enum';
import { ResponsiveService } from '../../../services/responsive.service';
import { SidenavHelperService } from '../../services/sidenav-helper.service';
import { RNDataTransferService } from 'src/app/common/services/rn-data-transfer.service';
import {
  Event as EventList,
  MType,
  MType as MtypeList,
} from '../../../../pusher-event';
import { ORDER_BY } from '../../../../order/platform.constant';
import { CommonService } from 'src/app/common/services/common.service';
import { LanguageService } from 'src/app/common/services/language.service';
import {
  RESTAURANT,
  EE_HQ,
  COGWHEEL,
  LOGOUT,
  SIDENAV_FMH_LOGO,
  LOGOUT_ICON,
  FMH_BACKGROUND,
  LOCK_ICON,
  ARROW_DOWN,
  ARROW_DOWN_ACTIVE,
  LOGOUT_ICON_HOVER,
  ARROW_LEFT,
  ARROW_RIGHT,
  LOCK_ICON_HOVER
} from '../../../../../defaultImages.js';
import { SidenavItemId } from '../../enums/sidenav.enum';

declare function send_data(params: string): any;

@Component({
  selector: 'app-modern-sidenav',
  templateUrl: './modern-sidenav.component.html',
  styleUrls: ['./modern-sidenav.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ModernSidenavComponent
  implements OnInit, OnDestroy, AfterViewInit
{
  @HostBinding('class.hq-mode') get isHqMode(): boolean {
    return this._hqService.isHqUser();
  }

  @HostBinding('class.non-hq-mode') get isNonHqMode(): boolean {
    return !this._hqService.isHqUser();
  }

  @Output() change = new EventEmitter();
  @Output() onToggleCollapse = new EventEmitter();
  @Output() onMobileClose = new EventEmitter(); // New event for mobile close

  // Component state
  private readonly destroy$ = new Subject<void>();
  private subscription = new Subscription();

  // Observables
  public readonly config$: Observable<SidenavConfiguration>;
  public readonly state$: Observable<SidenavState>;
  public filteredGroups$: Observable<SidenavGroup[]>;

  // Form controls
  public readonly darkTheme = new FormControl(false);

  // Essential properties for compatibility
  public restaurantDetails: any = {};
  public userType = '';
  public restaurant_name = '';
  public restaurantid: string = '';
  public currentUrl = '';
  public isMobile = false;
  public isHqUser: boolean = false;
  public billingCounterName = '';
  public hqLogo = '';
  public showLoyalty = false;
  public hideTooltip: boolean = false;
  public isLockScreenEnabled: boolean = false;

  // Notification count properties
  public validOrders: any[] = [];
  public allNotification: any[] = [];
  public shouldDisableSidenav: boolean = false;
  public isHovering: boolean = false;

  // Images
  public readonly images = {
    RESTAURANT,
    EE_HQ,
    COGWHEEL,
    LOGOUT,
    SIDENAV_FMH_LOGO,
    LOGOUT_ICON,
    FMH_BACKGROUND,
    LOCK_ICON,
    ARROW_DOWN,
    ARROW_DOWN_ACTIVE,
    LOGOUT_ICON_HOVER,
    ARROW_LEFT,
    ARROW_RIGHT,
    LOCK_ICON_HOVER,
  };

  // Core business properties
  public noOfNewOrders: number = 0;
  newTableEvents: number = 0;
  onGoingRequest = 0;
  allFoodCourtOrderCount: number = 0;
  tableBookingLength: number = 0;
  private viewInitialized = false;
  private pusherEventsWatching = false;
  private tableBookingsRequestInFlight = false;
  outletList: any[] = [];
  allOutletListIds = [];
  isFoodCourtViewEnabled: boolean = false;
  validPrepaidOrderTypes: Array<number> = [];
  isAutoRefreshEnabled: boolean = false;
  isPrepaidOrderingEnabled: boolean = false;
  isAdminUser: boolean = false;
  refreshInterval: any;
  isHoveringLogout: boolean = false;
  isHoveringLock: boolean = false;

  constructor(
    private _router: Router,
    private _cdr: ChangeDetectorRef,
    public _configService: SidenavConfigService,
    private _groupsService: SidenavGroupsService,
    public _lockService: SidenavLockService,
    private _authService: AuthService,
    public _utilService: UtilService,
    public _hqService: EasyEatHqService,
    private _localStorage: LocalStorageService,
    private _eventTracker: EventTrackerService,
    private _themeService: ThemeService,
    private _counterDataService: BillingCountersDataService,
    private _apiService: ApiService,
    private _tableBookingHelperService: TableBookingHelperService,
    private _http: HttpClient,
    private _pusherEvent: PusherEventService,
    private _orderState: OrderStateService,
    private _orderReactEvent: OrderReactEventService,
    private _translateService: TranslateService,
    private _animationControlService: AnimationControlService,
    private _msgService: MessageService,
    private _dateService: DateService,
    private _audioService: AudioService,
    private _tbApiService: TableBookingApiService,
    private _dialog: MatDialog,
    private _sidenavHelperService: SidenavHelperService,
    private _responsiveService: ResponsiveService,
    private _rnDataTransferService: RNDataTransferService,
    private _commonService: CommonService,
    private _languageService: LanguageService
  ) {
    this._languageService.setCountryData();

    // Must assign before setupSidenavVisibility — restaurant details may already be
    // cached after returning from /pos, so initView can run synchronously in the constructor.
    this.config$ = this._configService.config$;
    this.state$ = this._configService.state$;

    this.ensureRestaurantDetailsCached();
    this.setupSidenavVisibility();

    this.outletList = JSON.parse(this._localStorage.get('ee_outlets') || '[]');

    // Initialize basic properties
    this.isHqUser = this._hqService.isHqUser();
    this.isMobile = window.innerWidth < 768;

    // Watch route changes
    this._router.events
      .pipe(
        filter(
          (event: RouterEvent): event is NavigationEnd =>
            event instanceof NavigationEnd
        ),
        takeUntil(this.destroy$)
      )
      .subscribe((event: NavigationEnd) => {
        this.currentUrl = event.url;
        this._cdr.markForCheck(); // Trigger change detection when route changes
      });

    // Initialize current URL
    this.currentUrl = this._router.url;

    // Watch theme changes
    this.darkTheme.valueChanges.subscribe((value) => {
      if (this._themeService.isDarkTheme()) {
        this._themeService.setLightTheme();
      } else {
        this._themeService.setDarkTheme();
      }
    });
  }

  ngOnInit(): void {}

  ngAfterViewInit(): void {
    // Safety net after /pos destroy+recreate if init was skipped.
    if (
      !this.viewInitialized &&
      this._localStorage.get('token') &&
      (this._commonService.restaurantDetails$.value ||
        this._localStorage.get('restaurant_id'))
    ) {
      this.shouldDisableSidenav =
        (this._commonService.restaurantDetails$.value?.settings?.global
          ?.is_realm_enabled ??
          false) && this._rnDataTransferService.isReactNativeApp();
      if (!this.shouldDisableSidenav) {
        this.initView();
      }
    }
    if (!this.shouldDisableSidenav) {
      setTimeout(() => {
        this._cdr.markForCheck();
      }, 100);
    }
  }

  ngOnDestroy(): void {
    if (this.subscription) {
      this.subscription.unsubscribe();
    }
    // Do not clear shared config here — recreate on return from /pos must rebuild
    // groups via refreshConfigurationForUserTypeChange() in initView.
    this.destroy$.next();
    this.destroy$.complete();
  }

  private ensureRestaurantDetailsCached(): void {
    if (this._commonService.restaurantDetails$.value) {
      return;
    }
    try {
      const raw = JSON.parse(
        this._localStorage.get('restaurant_details') || '{}'
      );
      const data = raw?.data?.id ? raw.data : raw?.id ? raw : null;
      if (data) {
        this._commonService.setRestaurantDataObs(data);
      }
    } catch {}
  }

  private setupSidenavVisibility(): void {
    this._commonService
      .getRestaurantDataObs()
      .pipe(takeUntil(this.destroy$))
      .subscribe((res) => {
        if (res) {
          this.shouldDisableSidenav =
            (res?.settings?.global?.is_realm_enabled ?? false) &&
            this._rnDataTransferService.isReactNativeApp();
          // Restaurant details emit often; only initialize sidenav once per instance.
          if (!this.shouldDisableSidenav && !this.viewInitialized) {
            this.refreshInterval = null;
            this.initView();
          }
          this._cdr.markForCheck();
        }
      });
  }

  private initView(): void {
    if (this.viewInitialized) {
      return;
    }
    this.viewInitialized = true;

    // Needed for template *ngIf on recreate after /pos.
    this.userType = this._localStorage.get('user_type') || '';
    this.restaurant_name = this._localStorage.get('restaurant_name') || '';
    this.restaurantid = this._localStorage.get('restaurant_id') || '';
    this.restaurantDetails =
      this._commonService.restaurantDetails$.value || this.restaurantDetails;

    this.setTranslations();
    this.setupSubscriptions();
    this.initializeBasicInfo();
    this.checkIfHqSwitchVisible();
    this.getRestaurantDataObs();
    this.setNotificationListener();
    this.isLockScreenEnabled = !!this._utilService.isLockEnabled();

    // Rebuild nav groups after component recreate (e.g. returning from Create Order).
    this._configService.refreshConfigurationForUserTypeChange();
    this.config$.pipe(take(1)).subscribe((config) => {
      if (config?.groups?.length) {
        this._groupsService.updateGroups(config.groups);
      }
    });
    this.initializeFilteredGroups();

    this.config$?.pipe(takeUntil(this.destroy$)).subscribe((config) => {
      if (!config?.groups?.length) {
        return;
      }
      this._groupsService.groups$.pipe(take(1)).subscribe((existingGroups) => {
        if (
          existingGroups?.length === 0 ||
          this.hasGroupsChanged(existingGroups, config.groups)
        ) {
          this._groupsService.updateGroups(config.groups);
        }
        this._cdr.markForCheck();
      });
    });

    this.setupBusinessLogic();

    setTimeout(() => {
      this._cdr.detectChanges();
    }, 0);
  }

  private getRestaurantDataObs(): void {
    const param = new FormData();
    param.append('restaurant_id', localStorage.getItem('restaurant_id'));
    this._apiService.getRestaurantDetails(param).subscribe((res) => {
      this.updateUI(res);
      if (res?.status !== 0) {
        this._localStorage.set(
          'restaurant_currency',
          res?.data?.curr_sym || 'RM'
        );
      }
    });
  }

  private setRestaurantDetails() {
    this._commonService
      .getRestaurantDataObs()
      .pipe(takeUntil(this.destroy$))
      .subscribe(
        (res) => {
          this.restaurantDetails = res;
          // Store restaurant logo in localStorage for consistency
          if (res?.data) {
            this._localStorage.set(
              'restaurant_logo',
              this.restaurantDetails?.logo
            );
            this._localStorage.set(
              'restaurant_currency',
              this.restaurantDetails?.curr_sym
            );
            this._localStorage.set(
              'restaurant_address',
              this.restaurantDetails?.address
            );
            this._localStorage.set(
              'restaurant_location',
              JSON.stringify(this.restaurantDetails?.location)
            );
            this._localStorage.set(
              'restaurant_type',
              this.restaurantDetails?.type
            );
            this._localStorage.set(
              'restaurant_settings',
              JSON.stringify(this.restaurantDetails?.settings)
            );
            this._localStorage.set(
              'restaurant_nameid',
              this.restaurantDetails?.nameid
            );
          }
          this.userType = this._localStorage.get('user_type') || '';
          this.restaurant_name =
            this._localStorage.get('restaurant_name') || '';
          this.restaurantid = this._localStorage.get('restaurant_id') || '';
          this._cdr.markForCheck();
        },
        (error) => {
          console.error('Error getting restaurant data', error);
        }
      );
  }

  updateUI(res) {
    if (res?.status) {
      this.setRestaurantDetails();
      this.restaurantDetails = res?.data;
      this._localStorage.set('restaurant_address', res?.data?.address);
      this._localStorage.set(
        'restaurant_location',
        JSON.stringify(res?.data?.location)
      );
      this._localStorage.set(
        'restaurant_currency',
        res?.data?.curr_sym ?? 'RM'
      );
      this._localStorage.set('restaurant_type', res['data']['type']);
      this._localStorage.set(
        'restaurant_settings',
        JSON.stringify(res.data.settings)
      );
      this._localStorage.set(
        'restaurant_printer',
        this._utilService.getPrinterIp(res?.data)
      );
      this._localStorage.set(
        'default_bill_receipt_ip',
        res.data?.printer || ''
      );
      this._localStorage.set('restaurant_logo', res.data.logo);
      this._localStorage.set('restaurant_nameid', res.data.nameid);
      this._localStorage.set('dkick_code', JSON.stringify(res.data.dkick_code));
    }
  }

  private setNotificationListener(): void {
    this.subscription.add(
      this._commonService.edit$.subscribe((message) => {
        if (message === 'tableservice updated') {
          this.get_notification(SidenavItemId.TABLE_SERVICE);
        } else if (message == 'order completed') {
          this.calculateRunningOrders();
        }
      })
    );
  }

  private initializeBasicInfo(): void {
    this.hqLogo = this._localStorage.get('hq_logo') || '';
  }

  private setupSubscriptions(): void {
    // Update locked features periodically
    this._lockService.lockedFeatures$
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        this._cdr.markForCheck();
      });

    // Watch responsive changes if service is available
    try {
      this._responsiveService.mediaBreakpoint$
        .pipe(takeUntil(this.destroy$))
        .subscribe((mediaBreakpoint: string) => {
          this.isMobile = mediaBreakpoint === 'xs';
          this._cdr.markForCheck();
        });
    } catch (e) {
      console.error('Error watching responsive changes:', e);
    }
  }

  private initializeFilteredGroups(): void {
    const userType = this.getUserTypeEnum();

    // Make filteredGroups$ reactive to route changes and collapse state
    this.filteredGroups$ = combineLatest([
      this._router.events.pipe(
        filter(
          (event): event is NavigationEnd => event instanceof NavigationEnd
        ),
        map((event) => event.url),
        startWith(this.currentUrl)
      ),
      this.state$.pipe(map((state) => state.isCollapsed)),
      // Add config$ to trigger re-evaluation when configuration changes
      this.config$.pipe(map((config) => config.groups.length)),
    ]).pipe(
      switchMap(([activeRoute, isCollapsed, groupsCount]) => {
        return this._groupsService.getFilteredGroups$(
          userType,
          isCollapsed,
          activeRoute,
          '',
          this.getItemNotificationCount.bind(this)
        );
      }),
      takeUntil(this.destroy$)
    );

    // Subscribe to trigger change detection when groups update
    this.subscription.add(
      this.filteredGroups$.subscribe((groups) => {
        this._cdr.markForCheck();
      })
    );
  }

  private getUserTypeEnum(): UserType {
    const userType = this.getUserType();
    switch (userType) {
      case UserType.HQ:
        return UserType.HQ;
      case UserType.KIOSK:
        return UserType.KIOSK;
      case UserType.POS:
      default:
        return UserType.POS;
    }
  }

  // Public methods for template
  public onItemClick(event: {
    event: string;
    item: SidenavItem;
    nativeEvent?: Event;
  }): void {
    const { item, nativeEvent } = event;

    // Check if item is locked
    if (this._lockService.isFeatureLocked(item.id)) {
      this._lockService.handleLockedFeature(item.id, nativeEvent);
      return;
    }

    // If sidenav is collapsed and on desktop, only expand it (don't navigate)
    if (this.getIsCollapsed() && !this.isMobile) {
      this._configService.toggleCollapse();
      // Emit the toggle event
      const newCollapsedState =
        this._configService.getCurrentState().isCollapsed;
      this.onToggleCollapse.emit({ isCollapsed: newCollapsedState });
      this._cdr.markForCheck();

      // Don't navigate when expanding from collapsed state
      // Just track the click and emit change event
      this.trackItemClick(item);
      this.change.emit();
      return;
    }

    // Navigate normally when sidenav is expanded or on mobile
    this.navigateToItem(item);

    // Track item click
    this.trackItemClick(item);

    // On mobile, close the sidenav after navigation
    if (this.isMobile) {
      this.onMobileClose.emit();
    } else {
      // Emit change event for desktop
      this.change.emit();
    }
  }

  private navigateToItem(item: SidenavItem): void {
    // Handle navigation
    if (item.route) {
      this._router.navigate([item.route]);
    } else if (item.onClick) {
      item.onClick();
    }
  }

  public onGroupToggle(event: { event: string; group: SidenavGroup }): void {
    // If sidenav is collapsed and on desktop, only expand it (don't toggle group)
    if (this.getIsCollapsed() && !this.isMobile) {
      this._configService.toggleCollapse();
      // Emit the toggle event
      const newCollapsedState =
        this._configService.getCurrentState().isCollapsed;
      this.onToggleCollapse.emit({ isCollapsed: newCollapsedState });
      this.navigateToItem(event?.group?.items[0]);
      this._cdr.markForCheck();
      return;
    }

    // Toggle group normally when sidenav is expanded or on mobile
    this._groupsService.toggleGroup(event.group.id);
    this._eventTracker.easyeat_track_event('SIDENAV_GROUP_TOGGLED', {
      group_id: event.group.id,
      expanded: !event.group.isExpanded,
    });
    // Trigger change detection to update the UI
    this._cdr.markForCheck();
  }

  public onCollapseToggleClick(): void {
    // On mobile, close the sidenav completely
    if (this.isMobile) {
      this.onMobileClose.emit();
      return;
    }

    // On desktop/tablet, toggle collapsed state
    this._configService.toggleCollapse();
    // Get the new state after toggling
    const newCollapsedState = this._configService.getCurrentState().isCollapsed;
    this.onToggleCollapse.emit({ isCollapsed: newCollapsedState });
    this._cdr.markForCheck();
  }

  public onThemeToggle(darkMode: boolean): void {
    if (darkMode) {
      this._themeService.setDarkTheme();
    } else {
      this._themeService.setLightTheme();
    }

    this._eventTracker.easyeat_track_event('THEME_TOGGLED', {
      dark_mode: darkMode,
    });
  }

  public logout(): void {
    const userType = this._localStorage.get('ee_user_type');
    this._localStorage.clearSession();
    this.finishLogout();

    if (userType === 'rest_hq_admin' || userType === 'rest_hq_staff') {
      this._router.navigateByUrl('/hq/login');
    } else {
      this._router.navigateByUrl('/login');
    }
  }

  private clearLocalStorage(): void {
    try {
      this._localStorage.clearSession();
      this.finishLogout();
    } catch (error) {}
  }

  private finishLogout(): void {
    try {
      this.userType = '';
      this.change.emit('logout');

      //React logout action
      try {
        let data = { action: 'logout' };
        let x = JSON.stringify(data);
        send_data(x);
      } catch (error) {}
    } catch (error) {}
  }

  public getIsCollapsed(): boolean {
    if (this.isMobile) {
      return false;
    }

    // Get the current state value from the configuration service
    return this._configService.getCurrentState().isCollapsed;
  }

  public getUserType(): string {
    if (this._hqService.isHqUser()) return 'HQ';
    if (this._utilService.isKiosk()) return 'KIOSK';
    return 'POS';
  }

  public trackItemClick(item: SidenavItem): void {
    this._eventTracker.easyeat_track_event('SIDENAV_ITEM_CLICKED', {
      item_id: item.id,
      item_label: item.label,
      route: item.route,
      user_type: this.userType,
    });
  }

  trackByGroupId(index: number, group: any): string {
    return group?.id;
  }

  trackByItemId(index: number, item: any): string {
    return item?.id;
  }

  // Add missing methods for template compatibility
  trackGroupById(index: number, group: any): string {
    return group?.id;
  }

  trackItemById(index: number, item: any): string {
    return item?.id;
  }

  onSwitchToggle(): void {
    const currentUserType = this.getUserType();
    if (currentUserType === 'HQ') {
      // Currently HQ user, switching to POS - redirect to POS login
      this._router.navigate(['/login']);
      this.clearLocalStorage();
    } else {
      // Currently POS user, switching to HQ - redirect to HQ login
      if (this._rnDataTransferService.isReactNativeApp()) {
        const hqLoginUrl = this._sidenavHelperService.getHqLoginUrl();
        this._rnDataTransferService.sendRNForOpenUrlInBrowser(hqLoginUrl);
      } else {
        this._router.navigate(['/hq/login']);
        this.clearLocalStorage();
      }
    }
  }

  isItemActive(item: SidenavItem, activeRoute: string): boolean {
    if (!item.route || !activeRoute) return false;

    // Strip query parameters and fragments from the active route
    const cleanActiveRoute = activeRoute.split('?')[0].split('#')[0];

    // Remove leading slash for consistent comparison
    const itemRoute = item.route.startsWith('/')
      ? item.route.substring(1)
      : item.route;
    const currentRoute = cleanActiveRoute.startsWith('/')
      ? cleanActiveRoute.substring(1)
      : cleanActiveRoute;

    // Exact match first
    if (currentRoute === itemRoute) return true;

    // Check if current route starts with item route (for sub-routes)
    if (currentRoute.startsWith(itemRoute + '/')) return true;

    // Special cases for complex routes
    if (itemRoute.includes('/') && currentRoute.includes('/')) {
      const itemBasePath = itemRoute.split('/')[0];
      const currentBasePath = currentRoute.split('/')[0];
      return (
        itemBasePath === currentBasePath && currentRoute.includes(itemRoute)
      );
    }

    return false;
  }

  getItemNotificationCount(item: SidenavItem): number {
    return this._configService.getNotificationCountForItem(item?.id);
  }

  /**
   * Get the appropriate icon for an item based on its active state
   */
  getItemIcon(item: SidenavItem): string {
    if (this.isItemActive(item, this.currentUrl) && item.selectedIcon) {
      return item.selectedIcon;
    }
    return item.icon;
  }

  /**
   * Get the appropriate icon for a group based on whether it has active items
   */
  getGroupIcon(group: SidenavGroup): string {
    if (group.hasActiveItem && group.selectedIcon) {
      return group.selectedIcon;
    }
    return group.icon || '';
  }

  getGroupNotificationCount(group: SidenavGroup): number {
    return group.childrenCount || 0;
  }

  onSwitchToFMH(): void {
    const saasCompanyId = this._localStorage.get('saas_company_id');
    if (saasCompanyId) {
      if (this._rnDataTransferService.isReactNativeApp()) {
        this._rnDataTransferService.sendRNForRedirectToStore(
          this._sidenavHelperService.getAndoidfmhUrl(),
          this._sidenavHelperService.getIosfmhUrl()
        );
      } else {
        const fmhAuthUrl = this._sidenavHelperService.getFmhAuthUrl();
        window.open(fmhAuthUrl, '_blank', 'noopener,noreferrer');
      }
    } else {
      this.openFMHOnboardModal();
    }
  }

  onLockScreen(): void {
    this._router.navigateByUrl('/lock');
    this._eventTracker.easyeat_track_event('LOCK_SCREEN_CLICKED', {
      user_type: this.userType,
    });
  }

  // Remaining original sidenav methods that are actually used
  isSettlementVisible(x: any): boolean {
    if (((x || {}).settings || {}).settlement.visible) return true;
    return false;
  }

  private setTranslations(): void {
    this.subscription.add(
      this._translateService.get('SIDENAV').subscribe((translation) => {})
    );
  }

  getReservationSettings(): any {
    const restSettings = this._utilService.getRestaurantSettings() || {};
    return restSettings.reservation || {};
  }

  watchPusherEvents(): void {
    if (this.pusherEventsWatching) {
      return;
    }
    this.pusherEventsWatching = true;

    // Watch order events
    this._pusherEvent
      .watchEvent(EventList.ORDER_EVENT)
      .pipe(takeUntil(this.destroy$))
      .subscribe((event) =>
        this.new_message_on_restaurant_topic(JSON.stringify(event))
      );

    // Watch table events
    this._pusherEvent
      .watchEvent(EventList.TABLE_EVENT)
      .pipe(takeUntil(this.destroy$))
      .subscribe(
        (event) => {
          this.new_message_mqtt_table_event(JSON.stringify(event));
        },
        (error) => {
          console.error('error', error);
        }
      );

    // Watch table reservation events
    this._pusherEvent
      .watchEvent(EventList.TABLE_RESERVATION_EVENT)
      .pipe(takeUntil(this.destroy$))
      .subscribe((event) => this.handleTableReservationEvents(event));
  }

  watchHQFoodCourtEvents(): void {
    this._pusherEvent
      .watchEvent(EventList.ORDER_EVENT)
      .pipe(takeUntil(this.destroy$))
      .subscribe((event) => this.onNewHQFoodCourtOrderEvent(event));
  }

  new_message_on_restaurant_topic(message: string): void {
    const message_obj = JSON.parse(message);
    this.isValidOrder(message_obj);
  }

  new_message_mqtt_table_event(message: string): void {
    const parsedMessage = JSON.parse(message);
    if (
      parsedMessage['mtype'] === MType.USER_MESSAGE ||
      parsedMessage['mtype'] === MType.PAYMENT_SUCCESS ||
      parsedMessage['mtype'] === MType.COLLECT_CASH
    ) {
      if (parsedMessage['mtype'] !== MType.COLLECT_CASH) {
        this._audioService.playAudioOnNewOrder();
      }
      this.getNotification();
    }
  }

  private handleTableReservationEvents(event: any): void {
    const triggerEventTypes = [
      MtypeList.RESV_ACCEPTED,
      MtypeList.RESV_REJECTED,
      MtypeList.RESV_PENDING,
      MtypeList.RESV_CREATED,
    ];
    if (triggerEventTypes.includes(event.mtype)) {
      if (event.mtype !== MtypeList.RESV_REJECTED) {
        if (
          !Boolean(this.getReservationSettings().auto_accept) &&
          (event.mtype === MtypeList.RESV_CREATED ||
            event.mtype === MtypeList.RESV_PENDING)
        ) {
          this._animationControlService.updateAnimationControl(
            ANIMATION_CONTROL_SUBJECT.TABLE_BOOKING,
            true
          );
        }

        if (
          (!Boolean(this.getReservationSettings().auto_accept) &&
            event.mtype !== MtypeList.RESV_ACCEPTED) ||
          Boolean(this.getReservationSettings().auto_accept)
        ) {
          this._audioService.playAudioOnNewOrder();
          this._msgService.showSuccess('Table booking received');
        }
      }
      this.getAllTableBookings();
    }
  }

  private isValidOrder(message: any): boolean {
    const prepaidDineinEnabled = this.validPrepaidOrderTypes.includes(0);
    try {
      if (message['mtype'] === MType.NEW_ORDER_PLACED) {
        if (
          message['data']['order_type'].toString() === '0' ||
          (message['data']['order_type'].toString() === '2' &&
            message['data']['takeaway_cash_enable'] === 1)
        ) {
          if (!(prepaidDineinEnabled && message['data']['order_type'] === 0)) {
            if (this.isAutoRefreshEnabled) {
              this._orderState.updateOrdersState(
                message,
                'from_pusher',
                message['mtype']
              );
            } else {
              !this._orderReactEvent.isOrderApplicableForAutoAccept(
                message['data']['order_type']
              ) && this._audioService.playAudioOnNewOrder(true);
              this._orderReactEvent.sendAcceptDeclineOrderReact(
                (message.data || {}).order_id || null,
                REACTEVENTS.ORDER_CREATE,
                { itr: message.data.itr }
              );
            }
          }
          this.calculateRunningOrders();
          this._orderReactEvent.sendAcceptDeclineOrderReact(
            (message.data || {}).order_id || null,
            REACTEVENTS.ORDER_CREATE,
            { itr: message.data.itr }
          );
        }
      } else if (message['mtype'] === MType.ORDER_UPDATED) {
        if (message['data']['order_type'].toString() === '0') {
          this.isAutoRefreshEnabled
            ? this._orderState.updateOrdersState(
                message,
                'from_pusher',
                message['mtype']
              )
            : !this._orderReactEvent.isOrderApplicableForAutoAccept(
                message['data']['order_type']
              ) && this._audioService.playAudioOnNewOrder(true);

          (message.data || {}).action_type === 'itr_update' &&
            this._orderReactEvent.sendAcceptDeclineOrderReact(
              message['data']['order_id'],
              REACTEVENTS.ORDER_UPDATE,
              { itr: message['data']['itr'] }
            );
        }
      } else if (
        message['mtype'] === MType.ORDER_COMPLETED ||
        message['mtype'] === MType.ORDER_CANCELED ||
        message['mtype'] === MType.ORDER_CONFIRMED
      ) {
        this.calculateRunningOrders();
      } else if (message['mtype'] === 'PAYACK') {
        this.getNotification();
      } else if (message['mtype'] === MType.PAYMENT_SUCCESS) {
        if (
          message['data']['order_type'].toString() === '1' ||
          message['data']['order_type'].toString() === '2'
        ) {
          !this.isAutoRefreshEnabled &&
            !this._orderReactEvent.isOrderApplicableForAutoAccept(
              message['data']['order_type']
            ) &&
            this._audioService.playAudioOnNewOrder(true);
          this.calculateRunningOrders();
          this._orderReactEvent.sendAcceptDeclineOrderReact(
            (message.data || {}).order_id || null,
            REACTEVENTS.ORDER_CREATE,
            { itr: message.data.itr }
          );
        } else if (
          message['data']['order_type'].toString() === '0' &&
          prepaidDineinEnabled
        ) {
          !this._orderReactEvent.isOrderApplicableForAutoAccept(
            message['data']['order_type']
          ) && this._audioService.playAudioOnNewOrder(true);
          this._orderReactEvent.sendAcceptDeclineOrderReact(
            (message.data || {}).order_id || null,
            REACTEVENTS.ORDER_CREATE,
            { itr: message.data.itr }
          );
        }
      }
      if (
        message['mtype'] === MtypeList.ORDER_CONFIRMED &&
        message['data']['platform'] == 'grabfood' &&
        message['data']['order_type'].toString() === '1'
      ) {
        this._audioService.playAudioOnNewOrder(true);
      }
    } catch (error) {
      console.error('Error processing order event:', error);
    }
    return true;
  }

  calculateRunningOrders(): void {
    const restaurantNameId = this._localStorage.get('restaurant_nameid');
    const isKiosk = this._localStorage.get('isKiosk') === '1';

    const api = !isKiosk
      ? {
          function: () =>
            this._apiService.getCurrentOrders(
              this.restaurantid,
              '',
              '',
              this.isFoodCourtViewEnabled
                ? this.allOutletListIds
                : [this.restaurantid]
            ),
        }
      : {
          function: () => this._apiService.getAllKioskOrders(restaurantNameId),
        };

    if (this.onGoingRequest === 0) {
      this.onGoingRequest = 1;
      api.function().subscribe(
        (res: any) => {
          this.onGoingRequest = 0;
          if (res?.statusCode === 200) {
            this.validOrders = [];
            for (const order of res.data) {
              if (
                !(
                  (order['order_type'] === 1 || order['order_type'] === 2) &&
                  order['is_paid'] === 1 &&
                  !hasOrderTopup(order)
                ) ||
                order['order_by'] === ORDER_BY.MM ||
                order['order_by'] === ORDER_BY.AUTO ||
                order['pay_with_cash'] === 1
              ) {
                this.validOrders.push(order);
              }
            }
            this.noOfNewOrders = this.validOrders.length;
            this._configService.updateNotificationCount(
              SidenavItemId.ORDER_DASHBOARD,
              this.noOfNewOrders
            );
            this._cdr.markForCheck();
          }
        },
        (error: any) => {
          this.onGoingRequest = 0;
        }
      );
    }
  }

  private getAllTableBookings(): void {
    const restId = this._localStorage.get('restaurant_id');
    if (!restId || this.tableBookingsRequestInFlight) {
      return;
    }

    const startEndEpochObj =
      this._tableBookingHelperService.getStartEndEpochByDate(
        this._dateService.getToday()
      );
    const params =
      this._tbApiService.getHttpParamsForAllTableBookings(startEndEpochObj);

    this.tableBookingsRequestInFlight = true;
    this._tbApiService.getAllTableBookingOrders(restId, params).subscribe(
      (response: any) => {
        this.tableBookingsRequestInFlight = false;
        if (response?.statusCode === 200) {
          this.tableBookingLength = response.data?.length || 0;
          this._cdr.markForCheck();
        }
      },
      (error) => {
        this.tableBookingsRequestInFlight = false;
        console.error('Error fetching table bookings:', error);
      }
    );
  }

  private getNotification(): void {
    const formData: any = new FormData();
    formData.append('token', this._localStorage.get('token'));

    // [PHP_TO_NEST_ROLLOUT] routed through ApiService so migration switch applies centrally
    this._apiService
      .getTableNotifications(formData)
      .subscribe(
        (data: any) => {
          this.populateNotification(data, SidenavItemId.TABLE_SERVICE);
        },
        (error) => {
          console.error(
            'Error while fetching notifications count',
            error?.message
          );
        }
      );
  }

  private populateNotification(notifications: any, id?: string): void {
    this.allNotification = [];
    if (notifications?.status === 1) {
      for (const notification of notifications.data || []) {
        if (notification.ack === 0) {
          this.allNotification.push({
            username: notification.sender_name,
            floor_name: notification.data?.floor_name || '',
            table_no: notification.data?.table_no || '',
            message_id: notification.mid,
            order_id: notification.data?.order_id || '',
            time_stamp: parseInt(notification.time_stamp),
            time_text: notification.time_text,
          });
        }
      }
    }
    this._configService.updateNotifications(this.allNotification, id);
    this.newTableEvents = this.allNotification.length;
    this._cdr.markForCheck();
  }

  private calculateAllFoodCourtOrders(): void {
    if (this.onGoingRequest === 0) {
      this.onGoingRequest = 1;
      this._apiService
        .getOrdersForMultipleRestaurants(
          this.allOutletListIds[0],
          '',
          '',
          this.allOutletListIds,
          1
        )
        .subscribe(
          (response: any) => {
            this.onGoingRequest = 0;
            if (response?.statusCode === 200) {
              const {
                food_is_ready_count,
                preparing_order_count,
                scheduled_orders_count,
                to_be_accepted_count,
              } = response.data;
              this.allFoodCourtOrderCount =
                (food_is_ready_count || 0) +
                (preparing_order_count || 0) +
                (scheduled_orders_count || 0) +
                (to_be_accepted_count || 0);
              this._configService.updateNotificationCount(
                SidenavItemId.ORDER_DASHBOARD,
                this.allFoodCourtOrderCount
              );
              this._cdr.markForCheck();
            }
          },
          (error: any) => {
            this.onGoingRequest = 0;
          }
        );
    }
  }
  // Pusher events handler for HQ Foodcourt orders
  onNewHQFoodCourtOrderEvent(message) {
    try {
      if (message['mtype'] === MtypeList.NEW_ORDER_PLACED) {
        if (
          message['data']['order_type'].toString() === '0' ||
          (message['data']['order_type'].toString() === '2' &&
            message['data']['takeaway_cash_enable'] === 1)
        ) {
          this._audioService.playAudioOnNewOrder();
          this.calculateAllFoodCourtOrders();
        }
      } else if (message['mtype'] === MtypeList.ORDER_UPDATED) {
        if (message['data']['order_type'].toString() === '0') {
          this._audioService.playAudioOnNewOrder();
        }
      } else if (
        message['mtype'] === MtypeList.ORDER_COMPLETED ||
        message['mtype'] === MtypeList.ORDER_CANCELED
      ) {
        this.calculateAllFoodCourtOrders();
      } else if (message['mtype'] === MtypeList.PAYMENT_SUCCESS) {
        if (
          message['data']['order_type'].toString() === '1' ||
          message['data']['order_type'].toString() === '2'
        ) {
          this._audioService.playAudioOnNewOrder();
          this.calculateAllFoodCourtOrders();
        }
      } else if (message['mtype'] === MtypeList.ITEM_PREPARED) {
        this._audioService.playAudioOnNewOrder();
      }
    } catch {}
  }

  hasRestDetailsViewPermission(): boolean {
    return this._authService.isURLAuthenticated('/restaurant-details');
  }

  isUsrLoggedIn(): boolean {
    return !!this._localStorage.get('token');
  }

  get_notification(id?: string): void {
    const formData: any = new FormData();
    formData.append('token', this._localStorage.get('token'));

    // [PHP_TO_NEST_ROLLOUT] routed through ApiService so migration switch applies centrally
    this._apiService
      .getTableNotifications(formData)
      .subscribe((data: any) => {
        this.populate_notification(data, id);
      });
  }

  populate_notification(notifications: any, id?: string): void {
    this.allNotification = [];
    if (notifications?.status === 1) {
      for (const notification of notifications.data || []) {
        if (notification.ack === 0) {
          this.allNotification.push({
            username: notification.sender_name,
            floor_name: notification.data?.floor_name || '',
            table_no: notification.data?.table_no || '',
            message_id: notification.mid,
            order_id: notification.data?.order_id || '',
            time_stamp: parseInt(notification.time_stamp),
            time_text: notification.time_text,
          });
        }
      }
    }
    this._configService.updateNotifications(this.allNotification, id);
    this._cdr.markForCheck();
  }

  private getAllReceiptPrinter(): void {
    const params = {
      restId: this._localStorage.get('restaurant_id'),
      deviceId: this._utilService.getDeviceUID(),
    };
    this._apiService
      .getAllReceiptPrinters(params.restId, params.deviceId)
      .subscribe(
        (response: any) => {
          if (response?.statusCode === 200) {
            this._counterDataService.setBillingCounterInfo(response?.data);
          }
        },
        (error) => {
          console.error(error?.error);
        }
      );
  }

  // Core business methods
  private setupBusinessLogic(): void {
    this.restaurantid = localStorage.getItem('restaurant_id');
    this.isHqUser = this._hqService.isHqUser();

    // Initialize food court settings
    try {
      this.isFoodCourtViewEnabled = !!JSON.parse(
        this._localStorage.get('fc_rest_ids') || '[]'
      );
      this.isAutoRefreshEnabled = !!((JSON.parse(
        this._localStorage.get('restaurant_settings')
      ) || {})['global'] || {})['auto_refresh'];
    } catch {}

    // Initialize prepaid order settings
    this.computePrepaidOrderSetting();

    // Initialize outlet list
    this.allOutletListIds = (this.outletList || []).map((item) => item['id']);

    // Initialize counts
    this.calculateRunningOrders();
    this.getAllTableBookings();
    this.getNotification();

    // Set up food court if applicable
    if (this.isFoodCourtViewEnabled) {
      this.calculateAllFoodCourtOrders();
    }

    // Setup appropriate pusher events
    if (this.isHqUser) {
      this.watchHQFoodCourtEvents();
    } else {
      this.watchPusherEvents();
    }
  }

  private computePrepaidOrderSetting(): void {
    try {
      const rest_settings = (JSON.parse(
        this._localStorage.get('restaurant_settings')
      ) || {})['global'];
      rest_settings &&
        (this.isPrepaidOrderingEnabled =
          !!rest_settings['enable_pre_paid_order']);

      if (this.isPrepaidOrderingEnabled) {
        const prePaidSettings = (rest_settings || {}).pre_paid_order || 2;
        this.validPrepaidOrderTypes =
          this._utilService.getValidOrderTypes(prePaidSettings);
      }
    } catch {}
  }

  private hasGroupsChanged(
    existingGroups: SidenavGroup[],
    newGroups: SidenavGroup[]
  ): boolean {
    if (existingGroups.length !== newGroups.length) {
      return true;
    }

    return existingGroups.some((group, index) => {
      const nextGroup = newGroups[index];
      if (group.id !== nextGroup?.id) {
        return true;
      }
      const existingItems = group.items.map(
        (item) => `${item.id}:${item.isVisible}`
      );
      const nextItems = (nextGroup.items || []).map(
        (item) => `${item.id}:${item.isVisible}`
      );
      return JSON.stringify(existingItems) !== JSON.stringify(nextItems);
    });
  }

  async openFMHOnboardModal(): Promise<void> {
    try {
      const { FmhOnboardModalComponent } = await import(
        '../fmh-onboard-modal/fmh-onboard-modal.component'
      );

      const dialogConfig: MatDialogConfig = {
        maxWidth: '600px',
        maxHeight: '700px',
        disableClose: false,
        hasBackdrop: true,
        backdropClass: 'backdrop',
        panelClass: 'fmh-onboard-modal-panel',
      };
      this._dialog.open(FmhOnboardModalComponent, dialogConfig);
    } catch (error) {
      console.error('Error opening FMH onboard modal:', error);
    }
  }

  checkIfHqSwitchVisible(): void {
    const roles = JSON.parse(this._localStorage.get('roles')) || [];
    const isAdmin = this._localStorage.get('ee_user_type');

    // Check if user has any administrative roles or is an owner
    this.isAdminUser =
      ['rest_admin', 'rest_hq_admin', 'ee_admin'].includes(isAdmin) ||
      roles.includes('owner');
  }
}
